import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApiBess } from '../api/ApiBess';

const DashboardContext = createContext();

const DashboardProvider = ({ children }) => {

  const {
    getAvailableClusters,
    getOverviewData,
    getGridData,
    getSolarData,
    getBatteryData,
    getPcsData,
    getComponentsTree,
    getUtilityBillData,
    getUtilityBillPeriods,
  } = useApiBess();

  const [clustersFetchCount, setClustersFetchCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [clustersList, setClustersList] = useState([]);
  const [clickedCluster, setClickedCluster] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);

  const [pollingCounter, setPollingCounter] = useState(0);

  const [overviewData, setOverviewData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [batteryData, setBatteryData] = useState(null);
  const [pcsData, setPcsData] = useState(null);
  const [solarData, setSolarData] = useState(null);

  const [componentsTree, setComponentsTree] = useState(null);

  const [utilityBillData, setUtilityBillData] = useState(null);
  const [utilityBillPeriods, setUtilityBillPeriods] = useState(null);

  const [lastClusterId, setLastClusterId] = useState(null);


  const cookie_name = "posh-ems-dashboard"

  useEffect(() => {
    resetCachedData();
  }, [selectedCluster])

  const resetCachedData = () => {
    // console.log("Resetting cache");
    setOverviewData(null);
    setGridData(null);
    setBatteryData(null);
    setPcsData(null);
    setComponentsTree(null);
  }

  const fetchAvailableClusters = async () => {
    setLoading(true);
    try {
      const clusters = await getAvailableClusters();
      setClustersList(clusters);
      setClustersFetchCount(prev => prev + 1);

      if (selectedCluster == null && clusters && clusters.length > 0) {
        setSelectedCluster(prev => clusters[0]);
        // console.log("Set default cluster");
        // console.log(clusters[0]);
      }

    } catch (error) {
      console.error('Failed to list clusters', error);
    } finally {
      setLoading(false);
    }
  }

  const selectClusterById = async (id) => {
    // Fetch the list if not yet ready
    // console.log("List:", clustersList);
    if (clustersList.length == 0) {
      await fetchAvailableClusters();
    }

    // console.log("Selected:", id);
    let found = false;
    if (clustersList.length > 0) {
      clustersList.forEach((cluster, index) => {
        // console.log("Cluster:", cluster);
        if (cluster.cluster_id == id) {
          // console.log("Found:", id);
          setSelectedCluster(prev => cluster);
          found = true;
        }
      });
    }
    return found;
  }

  const autoSelectCluster = async () => {
    console.log("Auto-selecting");

    if (clustersList.length > 0) {
      console.log("List:", clustersList)

      let found = false;
      if (lastClusterId != null) {
        clustersList.forEach((cluster, index) => {
          // console.log("Cluster:", cluster);
          if (cluster.cluster_id == lastClusterId) {
            // console.log("Found:", id);
            setSelectedCluster(prev => cluster);
            found = true;
          }
        });
      }

      if (found === false) {
        console.log("Pick first on the list");
        setSelectedCluster(prev => clustersList[0]);
      }
    } else {
      // Fetch the list if not yet ready
      await fetchAvailableClusters();
    }

  }


  const clickClusterById = (id) => {
    // console.log("Clicked:", id);
    clustersList.forEach((cluster, index) => {
      if (cluster.cluster_id === id) {
        // console.log("Found:", cluster);
        setClickedCluster(cluster);
        return;
      }
    })
  }

  const fetchClusterOverview = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const overview = await getOverviewData(selectedCluster.cluster_id);
        setOverviewData(overview);
        // console.log(selectedCluster);
        // console.log(overview);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster overview', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterGridData = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _gridData = await getGridData(selectedCluster.cluster_id);
        setGridData(_gridData);
        // console.log(selectedCluster);
        // console.log(_gridData);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster grid data', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterSolarData = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _solarData = await getSolarData(selectedCluster.cluster_id);
        setSolarData(_solarData);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster solar data', error);
    } finally {
      setLoading(false);
    }

  }
  const fetchClusterBatteryData = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _batData = await getBatteryData(selectedCluster.cluster_id);
        setBatteryData(_batData);
        // console.log(selectedCluster);
        // console.log(_gridData);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster battery data', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterPcsData = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _pcsData = await getPcsData(selectedCluster.cluster_id);
        setPcsData(_pcsData);
        // console.log(selectedCluster);
        // console.log(_gridData);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster overview', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterComponentsTree = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _tree = await getComponentsTree(selectedCluster.cluster_id);
        setComponentsTree(_tree);
        // console.log(_tree);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster overview', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterUtilityBillData = async (year, month) => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        setUtilityBillData(null); // Clear previous data while loading
        const _utilityBillData = await getUtilityBillData(selectedCluster.cluster_id, year, month);
        setUtilityBillData(_utilityBillData);
        // console.log(selectedCluster);
        // console.log(_utilityBillData);
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster utility bill data', error);
    } finally {
      setLoading(false);
    }
  }

  const fetchClusterUtilityBillPeriods = async () => {
    setLoading(true);
    try {
      if (selectedCluster && selectedCluster.cluster_id) {
        const _utilityBillPeriods = await getUtilityBillPeriods(selectedCluster.cluster_id);
        setUtilityBillPeriods(_utilityBillPeriods);
        // console.log(selectedCluster);
        console.log(_utilityBillPeriods);
        return _utilityBillPeriods;
      } else {
        console.warn('Cluster not selected yet');
      }
    } catch (error) {
      console.error('Failed to get cluster utility bill data', error);
    } finally {
      setLoading(false);
    }
    return null;
  }

  return (
    <DashboardContext.Provider
      value={{
        loading,
        fetchAvailableClusters, clustersFetchCount,
        clustersList,
        clickedCluster, clickClusterById,
        selectedCluster, selectClusterById,
        autoSelectCluster,
        pollingCounter, setPollingCounter,
        fetchClusterOverview, overviewData,
        fetchClusterGridData, gridData,
        fetchClusterBatteryData, batteryData,
        fetchClusterPcsData, pcsData,
        fetchClusterSolarData, solarData,
        fetchClusterComponentsTree, componentsTree,
        fetchClusterUtilityBillData, utilityBillData,
        fetchClusterUtilityBillPeriods, utilityBillPeriods,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

export const useDashboardContext = () => useContext(DashboardContext);

export default DashboardProvider;